import { useCallback, useState } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";

export const REQUEST_ERROR = "Something went wrong. Please try again.";

/**
 * Hook for requesting deactivation of the Connect Services integration.
 *
 * POSTs to `/add_ons/request_connect_service_deactivation`, which enqueues
 * `Transactly::RequestPartnerDeactivationJob` to open a HubSpot ticket for the
 * Transactly team. Nothing is deactivated here — the account keeps its
 * `transactly-connect-service` AccountProduct until their team reaches out and
 * handles it, so there is no state for the caller to reflect anywhere.
 *
 * Returns 202 once the job is queued, 422 when the account has no active
 * Connect Service, and 502 if the job cannot be enqueued. Neither failure is
 * actionable by the user, so both surface the same message.
 * `caseTransformingAxios` snake_cases the body (`additionalFeedback` ->
 * `additional_feedback`, matching the controller) and attaches the CSRF token
 * via its request interceptor.
 *
 * Returns:
 * - `requestCallback` — sends the request. Resolves `true` on success, `false`
 *   on failure. Never rejects.
 * - `error` — user-facing error message, or "" when there is nothing to show.
 * - `isSubmitting` — true while the request is in flight, for the button's
 *   loading state.
 * - `resetError` — clears a stale error message. Callers own the modal-open
 *   lifecycle, so this lets a reopen clear a leftover failure.
 */
export const useRequestConnectServiceDeactivation = () => {
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestCallback = async (reason: string, additionalFeedback: string) => {
    setError("");
    setIsSubmitting(true);

    try {
      await caseTransformingAxios.post("/add_ons/request_connect_service_deactivation", {
        reason,
        additionalFeedback,
      });

      return true;
    } catch {
      setError(REQUEST_ERROR);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetError = useCallback(() => setError(""), []);

  return { requestCallback, error, isSubmitting, resetError };
};

export default useRequestConnectServiceDeactivation;
